import Vue from "vue";
import axios from "axios";
import Vuex from "vuex";
import VuexORM from "@vuex-orm/core";
import VuexORMAxios from "@vuex-orm/plugin-axios";

import VueJwtDecode from "vue-jwt-decode";
import APILinks from "@/links/links.js";
import router from "../router/router";

axios.defaults.headers.common["Content-Type"] = "application/ld+json";
axios.defaults.headers.common["Accept"] = "application/ld+json";

// NOTE: use token | check expired token functionality | use refresh token
axios.interceptors.request.use(
  async config => {
    const token = localStorage.getItem("token");
    if (token && config.url !== APILinks.TOKEN_REFRESH()) {
      // set Authorization in headers
      config.headers.Authorization = "Bearer " + token;

      const jwtDecoded = VueJwtDecode.decode(token);
      // get current time in ms (jwt token format) & jwt token expiration date - 60 seconds
      const currentTime = new Date().valueOf() / 1000;
      const expirationDateOneMinuteBefore = jwtDecoded.exp - 60;
      // if current time is bigger than jwt token expiration date, execute refresh token
      if (currentTime >= expirationDateOneMinuteBefore) {
        // wait for this action to end, then go further
        try {
          await store.dispatch("auth/tokenRefresh");
        } catch (err) {
          const errorResponse = err.response;
          if (errorResponse && errorResponse.status == 401) {
            throw new axios.Cancel(err);
          }
        }
        // set Authorization in headers equal to the NEW token value (set by refresh token action)
        config.headers.Authorization = "Bearer " + localStorage.getItem("token");
      }
    }

    return config;
  },
  err => {
    return Promise.reject(err);
  }
);

axios.interceptors.response.use(
  res => {
    return res;
  },
  err => {
    const errorResponse = err.response;
    if (errorResponse && errorResponse.status == 401) {
      if (router.currentRoute.name !== "login") {
        router.push({ name: "login" }).catch(() => {
          // ..
        });
      }
    }
    return Promise.reject(err);
  }
);

import state from "./vuex/state.js";
import getters from "./vuex/getters.js";
import mutations from "./vuex/mutations.js";
import actions from "./vuex/actions.js";

import auth from "./vuex/modules/auth.js";
import votingLimitRules from "./vuex/modules/voting-limit-rules.js";
import meetingDetails from "./vuex/modules/meeting-details";
import countries from "./vuex/modules/countries.js";
import buildings from "./vuex/modules/buildings.js";
import adminNotification from "./vuex/modules/adminNotification.js";

import database from "./orm-axios/database.js";

Vue.use(Vuex);

VuexORM.use(VuexORMAxios, {
  axios,
  headers: {
    "Content-Type": "application/ld+json",
    Accept: "application/ld+json",
  },
});

const store = new Vuex.Store({
  plugins: [VuexORM.install(database)],
  state,
  getters,
  mutations,
  actions,
  modules: {
    auth,
    votingLimitRules,
    meetingDetails,
    countries,
    buildings,
    adminNotification,
  },
});

export default store;
